import { usePrivy } from "@privy-io/react-auth";
import { useAuth } from "contexts/authContext";
import { BigNumber, ethers } from "ethers";
import { ACCESS_TOKEN, getItem } from "local-storage";
import slugs from "navigation/slugs";
import { useCallback, useEffect, useState } from "react";
import { redirect } from "react-router";
import { useParams } from "react-router-dom";
import {
  getTiktokUserById,
  getTiktokUserHoldingsById,
  getUserProfileData,
  subscribe,
  unsubscribe,
} from "services/apis/apis";
import { appConfig } from "services/blockchain";
import abi from "abis/Fantech.json";
import useSWR from "swr";
import { formatAmount } from "utils/number/number";

const provider = appConfig.provider;
const fantechCA = appConfig.fantechAddress;
const fanTechContract = new ethers.Contract(fantechCA, abi, provider);
const defaultAmount = 1;
const chainId = appConfig.chainId;

export const useController = () => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { updateToken } = useAuth();
  const { id } = useParams();
  const [totalHolding, setTotalHolding] = useState(0);
  const { authenticated, user, logout } = usePrivy();
  const [isLoading, setIsLoading] = useState(true);
  // const [isLoading2, setIsLoading2] = useState(false);
  const [data, setData] = useState<any>();
  const [currentTab, setCurrentTab] = useState("Stream");
  // const [accountShares, setAccountShares] = useState(0);

  const { sendTransaction } = usePrivy();

  const subjectAddress = data?.sub_address;

  const handleUnsubscribe = async () => {
    try {
      const accessToken = getItem(ACCESS_TOKEN) || "";
      if (!accessToken) return;

      setIsSubscribed(false);
      const result = await unsubscribe(accessToken, data?.id);

      if (!result) {
        setIsSubscribed(true);
      }

      setData({ ...data, numFan: data?.numFan - 1 });
    } catch (error) {
      console.log(
        "🚀 ~ file: OtherProfile.tsx:51 ~ handleUnsubscribe ~ error:",
        error
      );
    }
  };

  const handleSubscribe = async () => {
    try {
      const accessToken = getItem(ACCESS_TOKEN) || "";
      if (!accessToken) return;

      setIsSubscribed(true);
      const result = await subscribe(accessToken, data?.id);

      if (!result) {
        setIsSubscribed(false);
      }

      setData({ ...data, numFan: data?.numFan + 1 });
    } catch (error) {
      console.log(
        "🚀 ~ file: OtherProfile.tsx:219 ~ handleSubscribe ~ error:",
        error
      );
    }
  };

  const handleBuyShare = async () => {
    try {
      const getPoolInInitialBuy = await fanTechContract.getPoolInInitialBuy(
        subjectAddress
      );

      let priceAfterFee;

      if (getPoolInInitialBuy) {
        priceAfterFee = await fanTechContract.getPoolInitialBuyPriceAfterFee(
          subjectAddress
        );
      } else {
        priceAfterFee = await fanTechContract.getBuyPriceAfterFee(
          subjectAddress,
          defaultAmount
        );
      }

      const uiConfig = {
        header: `Buy a ${data?.nickname || "Haska"} share`,
        description: `Buy 1 share for ${formatAmount(
          priceAfterFee,
          18,
          4
        )} ETH!`,
        buttonText: "Complete Purchase",
      };

      const abi = ["function buyShares(address sharesSubject)"];
      const iface = new ethers.utils.Interface(abi);
      const dataSMC = iface.encodeFunctionData("buyShares", [subjectAddress]);

      const unsignedTx = {
        to: fantechCA,
        chainId,
        data: dataSMC,
        value: priceAfterFee.toHexString(),
      };
      await sendTransaction(unsignedTx, uiConfig);
      mutateAccountShares();
      mutatePrice();
    } catch (error) {
      console.log(
        "🚀 ~ file: OtherProfile.tsx:84 ~ handleBuyShare ~ error:",
        error
      );
    }
  };

  const handleSellShare = async () => {
    let sellAfterFee = BigNumber.from(0);
    try {
      sellAfterFee = await fanTechContract.getSellPriceAfterFee(
        subjectAddress,
        defaultAmount
      );
    } catch (error) {
      console.log(
        "🚀 ~ file: OtherProfile.tsx:163 ~ handleSellShare ~ error:",
        error
      );
      return;
    }

    const uiConfig = {
      header: `Sell 1 ${data?.nickname || "Haska"} share`,
      description: `You will receive ${formatAmount(sellAfterFee, 18, 4)} ETH!`,
      buttonText: "Complete Sale",
    };

    try {
      const abi = [
        "function sellShares(address sharesSubject, uint256 amount)",
      ];

      const iface = new ethers.utils.Interface(abi);
      const dataSMC = iface.encodeFunctionData("sellShares", [
        subjectAddress,
        defaultAmount,
      ]);

      const unsignedTx = {
        to: fantechCA,
        chainId,
        data: dataSMC,
      };

      await sendTransaction(unsignedTx, uiConfig);
      mutatePrice();
      mutateAccountShares();
    } catch (error) {
      console.log(
        "🚀 ~ file: OtherProfile.tsx:185 ~ handleSellShare ~ error:",
        error
      );
    }
  };

  useEffect(() => {
    if (!authenticated) {
      updateToken("");
      redirect(slugs.login);
    }
  }, [authenticated, updateToken]);

  const getUserProfile = useCallback(async () => {
    if (!id) return;
    try {
      const accessToken = getItem(ACCESS_TOKEN) || "";
      if (!accessToken) return;
      const res = await getTiktokUserById(accessToken, id!);
      setData(res);

      if (res?.isSubscribe) {
        setIsSubscribed(true);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    getUserProfile();
  }, [getUserProfile]);

  useEffect(() => {
    const f = async () => {
      if (!id) return;
      try {
        const accessToken = getItem(ACCESS_TOKEN) || "";
        if (!accessToken) return;

        const res = await getTiktokUserHoldingsById(accessToken, id);
        setTotalHolding(res.length);
      } catch (error) {
        console.log("🚀 ~ file: Controller.ts:103 ~ f ~ error:", error);
      }
    };
    f();
  }, [id]);

  // useEffect(() => {
  //   if (!data?.sub_address) {
  //     return;
  //   }
  //   const f = async () => {
  //     try {
  //       setIsLoading2(true);
  //       const accessToken = getItem(ACCESS_TOKEN) || "";
  //       if (!accessToken) return;
  //       const res = await getUserProfileData(accessToken);

  //       const poolSupply: BigNumber = await fanTechContract.getAccountShares(
  //         data?.sub_address,
  //         res?.address
  //       );
  //       setAccountShares(poolSupply.toNumber());
  //     } catch (error) {
  //       console.log("🚀 ~ file: Controller.ts:115 ~ f ~ error:", error);
  //     } finally {
  //       setIsLoading2(false);
  //     }
  //   };
  //   f();
  // }, [data?.sub_address]);

  const {
    data: accountShares,
    isLoading: isLoading2 = true,
    mutate: mutateAccountShares,
  } = useSWR(
    ["accountShares", data?.sub_address],
    async () => {
      const accessToken = getItem(ACCESS_TOKEN) || "";
      if (!accessToken) return;
      const res = await getUserProfileData(accessToken);

      const result: BigNumber = await fanTechContract.getAccountShares(
        data?.sub_address,
        res?.address
      );
      return result.toNumber();
    },
    { shouldRetryOnError: false }
  );

  const { data: buyPrice = BigNumber.from(0), mutate: mutatePrice } = useSWR(
    ["price", data?.sub_address],
    async () => {
      const price = await fanTechContract.getBuyPriceAfterFee(
        data?.sub_address,
        defaultAmount
      );
      return price;
    },
    { shouldRetryOnError: false }
  );

  const { data: sellPrice = BigNumber.from(0) } = useSWR(
    ["sellPrice", data?.sub_address],
    async () => {
      const price = await fanTechContract.getSellPriceAfterFee(
        subjectAddress,
        defaultAmount
      );
      return price;
    }
  );

  const { data: poolSupply = 0, isLoading: isLoading3 = true } = useSWR(
    ["poolSupply", data?.sub_address],
    async () => {
      const poolSupply = await fanTechContract.getPoolSupply(data?.sub_address);
      return poolSupply.toNumber();
    },
    { shouldRetryOnError: false }
  );

  let status: "lock" | "lock_not_on_fan_tech" | "unlock" =
    "lock_not_on_fan_tech";

  /**
   * subject_address && pool share && accountShared = 0 -> lock
   *
   * !subject_address -> lock_not_on_fan_tech
   *
   * subject_address && pool share && accountShared > 0 -> unlock
   *
   */
  if (!data?.sub_address) {
    status = "lock_not_on_fan_tech";
  } else if (data?.sub_address && poolSupply && accountShares === 0) {
    status = "lock";
  } else if (data?.sub_address && poolSupply && accountShares! > 0) {
    status = "unlock";
  }

  return {
    handleUnsubscribe,
    handleSubscribe,
    user,
    data,
    currentTab,
    setCurrentTab,
    isLoading: isLoading || isLoading2 || isLoading3,
    status,
    totalHolding,
    isSubscribed,
    accountShares,
    handleBuyShare,
    handleSellShare,
    buyPrice,
    poolSupply,
    sellPrice,
  };
};
